import { API_URL, apiFetch, ContentError, parse } from "@/content/loader";
import {
	type Providers,
	ProvidersSchema,
	type SignedIn,
	SignedInSchema,
	type User,
	UserSchema,
} from "@/content/schema";

/** Authentication calls of the core API (session in an HttpOnly cookie, docs/AUTH.md). */

export type Provider = "google" | "apple";

/** The signed-in user, or null without a session. */
export async function getMe(signal?: AbortSignal): Promise<User | null> {
	try {
		return parse(UserSchema, await apiFetch("/auth/me", { signal }), "Compte");
	} catch (error) {
		if (error instanceof ContentError && error.status === 401) return null;
		throw error;
	}
}

export async function getProviders(signal?: AbortSignal): Promise<Providers> {
	return parse(
		ProvidersSchema,
		await apiFetch("/auth/providers", { signal }),
		"Connexion",
	);
}

/**
 * Where the Google / Apple button leads: the API sends the browser to the provider, which comes
 * back to the API, which sends it back to `next` once the session cookie is set.
 */
export function providerStartUrl(provider: Provider, next: string): string {
	return `${API_URL}/auth/${provider}/start?${new URLSearchParams({ next })}`;
}

/** Send a magic link to `email`; once used, it opens `next`. */
export async function startEmailLogin(
	email: string,
	next: string,
): Promise<void> {
	await apiFetch("/auth/email/start", {
		method: "POST",
		body: JSON.stringify({ email, next }),
	});
}

/** Use the token of a magic link: sets the session cookie. */
export async function verifyEmailLogin(token: string): Promise<SignedIn> {
	const data = await apiFetch("/auth/email/verify", {
		method: "POST",
		body: JSON.stringify({ token }),
	});
	return parse(SignedInSchema, data, "Connexion");
}

export async function logout(): Promise<void> {
	await apiFetch("/auth/logout", { method: "POST" });
}
